// src/pages/dataInput/DataInputPage.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Multi-step DataInput wizard.
//
//   DG  : 3 steps  (Source → Metadata → DG Config & Review)
//   DQ  : 2 steps  (Source → Metadata)
//   MDM : 2 steps  (Source → Metadata)
//
// Each source kind (file / ddl / db / catalog) emits a SourcePayload that
// Step 2 consumes uniformly. On submit we route to the correct backend
// endpoint based on kind, then poll the same status tracker as before.
//
// `embedded` prop: when true, drops the full-page header/footer chrome so
// the wizard fits inside an existing panel/modal.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from "react";
import { cls, injectTheme } from "../theme/theme";
import {
  adriva_uploadFiles,
  adriva_uploadFromConnection,
  adriva_uploadFromDDL,
  adriva_uploadFromCatalog,
  adriva_getProcessStatus,
} from "../API";
import { DGAppConfig } from "../dg/DGConfigModal";
import {
  SourcePayload, Tool, isFileSource, isDDLSource, isDBSource, isCatalogSource,
} from "./types";
import Step1Source       from "./Step1Source";
import Step2Metadata, { Step2Values } from "./Step2Metadata";
import Step3DGConfig     from "./Step3DGConfig";

// ── Pipeline-tracker types ───────────────────────────────────────────────────
type JobStatus = "yet_to_start" | "running" | "success" | "failed" | "skipped";
interface PipelineDef { job_type: string; label: string; depends_on: string[]; }
interface JobInfo { job_id: string; status: JobStatus; progress: number; message: string | null; start_time: string | null; end_time: string | null; }
interface Agent { agent_name: string; status: string; start_time: string; end_time: string; aggregate_usage: { total_tokens: number; cost_usd: number }; }
interface ProcessStatusResponse {
  process_id: string; status: string; message: string | null;
  jobs: Record<string, JobInfo>; pipeline_definition: PipelineDef[];
  full_tree: { start_time: string; end_time: string; agents: Agent[]; aggregate_usage: { total_tokens: number; cost_usd: number }; };
}

interface DataInputPageProps {
  tool: Tool;
  appNm?: string;
  action?: string;
  /** "page" (default), "embedded" (inside another card), "modal" (inside Bootstrap modal). */
  layout?: "page" | "embedded" | "modal";
  /** Back-compat — deprecated, prefer `layout`. */
  embedded?: boolean;
  onPipelineComplete?: (appNm: string) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const dur = (s?: string | null, e?: string | null) => {
  if (!s) return null;
  const sec = Math.round(((e ? new Date(e) : new Date()).getTime() - new Date(s).getTime()) / 1000);
  return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
};

const toSt = (raw?: string): JobStatus => {
  if (!raw) return "yet_to_start";
  const s = raw.toLowerCase();
  if (s === "success" || s === "completed") return "success";
  if (s === "failed" || s === "error")      return "failed";
  if (s === "running" || s === "started")   return "running";
  if (s === "skipped")                      return "skipped";
  return "yet_to_start";
};

// ─────────────────────────────────────────────────────────────────────────────

export const DataInputPage: React.FC<DataInputPageProps> = ({
  tool, appNm, action,
  layout, embedded,
  onPipelineComplete,
}) => {
  // Resolve layout — `embedded` prop is back-compat
  const mode: "page" | "embedded" | "modal" =
    layout ?? (embedded ? "embedded" : "page");

  useEffect(() => { injectTheme(); }, []);

  const isDG = tool === "BUILD_DG_USING_DATA";
  const totalSteps = isDG ? 3 : 2;

  // ── Wizard state ─────────────────────────────────────────────────────────
  const [step, setStep] = useState<number>(1);
  const [source, setSource] = useState<SourcePayload | null>(null);
  const [metadata, setMetadata] = useState<Step2Values>({
    appName:           appNm || "",
    systemName:        "",
    domain:            "",
    entity:            "",
    entityDescription: "",
    businessContext:   "",
    relationships:     [],
  });
  const [dgConfig, setDgConfig] = useState<DGAppConfig | null>(null);
  const [validatedStep, setValidatedStep] = useState<number>(0);

  // ── Submission / pipeline tracking ───────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [resp, setResp]             = useState<ProcessStatusResponse | null>(null);
  const [finalMsg, setFinalMsg]     = useState<string | null>(null);
  const [doneApp, setDoneApp]       = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Pre-populate source-derived defaults into metadata once a source is picked
  useEffect(() => {
    if (!source) return;
    setMetadata(prev => {
      const next = { ...prev };
      if (!next.systemName && isDBSource(source) && source.dbSource?.connection_name) {
        next.systemName = source.dbSource.connection_name.toUpperCase().replace(/\s+/g, "_");
      }
      if (!next.systemName && isCatalogSource(source) && source.catalogTarget) {
        next.systemName = `${source.catalogTarget.toUpperCase()}_IMPORT`;
      }
      if (next.relationships.length === 0 && source.relationships.length > 0) {
        next.relationships = [...source.relationships];
      }
      return next;
    });
  }, [source]);

  // ── Validation per step ──────────────────────────────────────────────────
  const step1Valid = !!source && (
    isFileSource(source)    ? (source.sheetConfigs ?? []).some(s => s.included) :
    isDDLSource(source)     ? (source.parsedTables ?? []).length > 0 :
    isDBSource(source)      ? !!source.dbSource :
    isCatalogSource(source) ? (source.catalogTables ?? []).length > 0 :
    false
  );

  const step2Valid = (() => {
    if (!source) return false;
    if (!metadata.appName.trim())         return false;
    if (!metadata.systemName.trim())      return false;
    if (!metadata.domain.trim())          return false;
    if (!metadata.businessContext.trim()) return false;
    if (source.sheetMode === "sub_entities") {
      if (!metadata.entity.trim())            return false;
      if (!metadata.entityDescription.trim()) return false;
    }
    return true;
  })();

  // ── Step navigation ──────────────────────────────────────────────────────
  const goNext = () => {
    if (step === 1 && !step1Valid) { setValidatedStep(1); return; }
    if (step === 2 && !step2Valid) { setValidatedStep(2); return; }
    setValidatedStep(0);
    setStep(s => Math.min(totalSteps, s + 1));
  };
  const goBack = () => setStep(s => Math.max(1, s - 1));
  const jumpTo = (n: number) => {
    if (n <= step) { setStep(n); return; }
    if (n === 2 && step1Valid) { setStep(2); return; }
    if (n === 3 && step1Valid && step2Valid && isDG) { setStep(3); return; }
  };

  // ── Polling helper ───────────────────────────────────────────────────────
  const isTerminal = (r: ProcessStatusResponse) =>
    ["completed", "failed", "success"].includes(r.status?.toLowerCase());

  const startPolling = (pid: string) => {
    pollingRef.current = setInterval(async () => {
      try {
        const r: ProcessStatusResponse = await adriva_getProcessStatus(pid);
        if (!r) return;
        setResp(r);
        if (isTerminal(r)) {
          if (pollingRef.current) clearInterval(pollingRef.current);
          setSubmitting(false);
          setFinalMsg(r.message || (r.status === "completed" ? null : "Pipeline ended."));
          const allDone = r.pipeline_definition
            .filter(d => d.job_type in r.jobs)
            .every(d => r.jobs[d.job_type]?.status === "success");
          if (allDone) {
            setDoneApp(metadata.appName);
            onPipelineComplete?.(metadata.appName);
          }
        }
      } catch {
        if (pollingRef.current) clearInterval(pollingRef.current);
        setSubmitting(false);
        setFinalMsg("Error fetching status.");
      }
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // ── Submit dispatcher ────────────────────────────────────────────────────
  const submit = async () => {
    if (!source || !step2Valid) {
      setValidatedStep(2);
      return;
    }

    setSubmitting(true);
    setResp(null);
    setFinalMsg(null);
    setDoneApp(null);

    try {
      let processId: string | undefined;

      if (isFileSource(source)) {
        const r = await adriva_uploadFiles({
          tool,
          action: action || "",
          app_nm: metadata.appName,
          systemName: metadata.systemName,
          business_context: metadata.businessContext,
          domain: metadata.domain,
          entity: metadata.entity,
          entity_description: metadata.entityDescription,
          csvFiles:      source.files.map(f => f.file),
          businessFiles: [],
          dg_config: tool === "BUILD_DG_USING_DATA" ? dgConfig : undefined,
          sheet_config: source.sheetConfigs.length > 0 ? JSON.stringify({
            mode: source.sheetMode,
            sheets: source.sheetConfigs.map(sc => ({
              fileName:    sc.fileName,
              sheetName:   sc.sheetName,
              displayName: sc.displayName,
              description: sc.description,
              included:    sc.included,
            })),
            relationships: metadata.relationships.map(r => ({
              fromSheet: r.from_table, fromCol: r.from_column,
              toSheet:   r.to_table,   toCol:   r.to_column,
            })),
          }) : "",
        });
        if (r?.success && r.process_id) {
          processId = r.process_id;
        } else {
          setSubmitting(false);
          setFinalMsg(r?.message || "Upload failed.");
          return;
        }
      }

      else if (isDDLSource(source)) {
        const r = await adriva_uploadFromDDL({
          tool,
          action: action || "",
          app_nm: metadata.appName,
          system_name: metadata.systemName,
          business_context: metadata.businessContext,
          domain: metadata.domain,
          entity: metadata.entity,
          entity_description: metadata.entityDescription,
          tables: source.parsedTables.map(t => ({
            table_name:  t.table_name,
            description: t.description,
            columns:     t.columns,
          })),
          relationships: metadata.relationships.map(r => ({
            from_table:  r.from_table,
            from_column: r.from_column,
            to_table:    r.to_table,
            to_column:   r.to_column,
          })),
          sheet_mode: source.sheetMode,
          dg_config:  tool === "BUILD_DG_USING_DATA" ? dgConfig : undefined,
        });
        if (r?.success && r.process_id) {
          processId = r.process_id;
        } else {
          setSubmitting(false);
          setFinalMsg(r?.message || "DDL upload failed.");
          return;
        }
      }

      else if (isDBSource(source)) {
        const r = await adriva_uploadFromConnection({
          tool,
          action: action || "",
          app_nm: metadata.appName,
          business_context: metadata.businessContext,
          domain: metadata.domain,
          entity: metadata.entity,
          system_name: metadata.systemName,
          connection_id: source.dbSource.connection_id,
          schema: source.dbSource.schema,
          table_schemas: source.dbSource.tableSchemas ?? {},
          tables: source.dbSource.tables,
          columns: source.dbSource.columns,
          relationships: source.dbSource.relationships,
          custom_sql: source.dbSource.custom_sql,
          row_limit: 500_000,
          dg_config: tool === "BUILD_DG_USING_DATA" ? dgConfig : undefined,
        });
        if (r?.success && r.process_id) {
          processId = r.process_id;
        } else {
          setSubmitting(false);
          setFinalMsg(r?.message || "Connection upload failed.");
          return;
        }
      }

      else if (isCatalogSource(source)) {
        const r = await adriva_uploadFromCatalog({
          tool,
          action: action || "",
          app_nm: metadata.appName,
          system_name: metadata.systemName,
          business_context: metadata.businessContext,
          domain: metadata.domain,
          entity: metadata.entity,
          entity_description: metadata.entityDescription,
          target: source.catalogTarget,
          source_label: source.catalogSourceLabel,
          tables: source.catalogTables,
          relationships: metadata.relationships.map(r => ({
            from_table:  r.from_table,
            from_column: r.from_column,
            to_table:    r.to_table,
            to_column:   r.to_column,
          })),
          sheet_mode: source.sheetMode,
          dg_config:  tool === "BUILD_DG_USING_DATA" ? dgConfig : undefined,
        });
        if (r?.success && r.process_id) {
          processId = r.process_id;
        } else {
          setSubmitting(false);
          setFinalMsg(r?.message || "Catalog import failed.");
          return;
        }
      }

      if (processId) startPolling(processId);
    } catch (err: any) {
      setSubmitting(false);
      setFinalMsg(err?.message || "An unexpected error occurred.");
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const showTracker = submitting || !!resp;
  const stepperLabels = isDG
    ? ["Source", "Metadata & relationships", "Governance config & review"]
    : ["Source", "Metadata & relationships"];

  const stepper = (
    <div className="gw-stepper">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map((n, idx) => {
        const status = n < step ? "done" : n === step ? "active" : "";
        return (
          <React.Fragment key={n}>
            {idx > 0 && <span className="gw-step-arrow">›</span>}
            <button
              className={cls("gw-step", status, "clickable")}
              type="button"
              onClick={() => jumpTo(n)}
            >
              <span className="gw-step-num">{n}</span>
              {stepperLabels[idx]}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className={cls(
        "gw-wizard",
        mode === "embedded" && "gw-wizard-embedded",
        mode === "modal"    && "gw-wizard-modal",
    )}>
        {mode === "page" && (
        <div className="gw-wizard-header">
            <div className="gw-wizard-eyebrow">
            {isDG ? "Data Governance Discovery"
            : tool === "BUILD_DQ_USING_DATA" ? "Data Quality Discovery"
            : "MDM Discovery"}
            </div>
            <h1 className="gw-wizard-title">Set up a new data source</h1>
            {stepper}
        </div>
        )}

        {(mode === "embedded" || mode === "modal") && (
        <div className="gw-wizard-stepper-only">{stepper}</div>
        )}

      <div className="gw-wizard-body">
        <div className={cls(mode === "page" && "gw-wizard-narrow")}>
          {step === 1 && (
            <Step1Source
              tool={tool}
              payload={source}
              onChange={setSource}
            />
          )}
          {step === 2 && source && (
            <Step2Metadata
              tool={tool}
              source={source}
              lockedAppName={appNm || undefined}
              values={metadata}
              validated={validatedStep === 2}
              onChange={setMetadata}
            />
          )}
          {step === 3 && source && isDG && (
            <Step3DGConfig
              source={source}
              metadata={metadata}
              dgConfig={dgConfig}
              onChange={setDgConfig}
            />
          )}

          {showTracker && (
            <div style={{ marginTop: 24 }}>
              <PipelinePanel
                resp={resp}
                completedApp={doneApp}
                onPipelineComplete={onPipelineComplete}
                finalMsg={finalMsg}
                submitting={submitting}
              />
            </div>
          )}
        </div>
      </div>

      <div className="gw-wizard-footer">
        <div
            className={cls(
                "gw-wizard-footer-msg",
                validatedStep === 1 && !step1Valid && "error",
                validatedStep === 2 && !step2Valid && "error",
                !!finalMsg && resp?.status === "failed" && "error",
            )}
            >
            {(() => {
                if (finalMsg) return resp?.status === "failed" ? `❌ ${finalMsg}` : finalMsg;
                if (validatedStep === 1 && !step1Valid) return "Pick a source and at least one table or sheet.";
                if (validatedStep === 2 && !step2Valid) return "Fill in all required fields before continuing.";
                if (step === 1) return step1Valid
                ? "Source ready — continue to metadata."
                : "Drop files, paste DDL, connect a database, or browse a catalog to begin.";
                if (step === 2) return step2Valid
                ? (isDG ? "Continue to governance config." : "Ready to run discovery.")
                : "Fill in application metadata to continue.";
                if (step === 3) return "Review and run discovery.";
                return "";
            })()}
            </div>

        <div className="gw-wizard-footer-actions">
          <button
            className="gw-btn gw-btn-ghost"
            onClick={goBack}
            disabled={step === 1 || submitting}
          >
            ← Back
          </button>
          {step < totalSteps && (
            <button className="gw-btn gw-btn-primary" onClick={goNext} disabled={submitting}>
              Continue →
            </button>
          )}
          {step === totalSteps && (
            <button
              className="gw-btn gw-btn-primary"
              onClick={submit}
              disabled={submitting || !step1Valid || !step2Valid}
            >
              {submitting ? "Running…" : "Run discovery →"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Pipeline tracker (unchanged)
// ─────────────────────────────────────────────────────────────────────────────

const PipelinePanel: React.FC<{
  resp: ProcessStatusResponse | null;
  submitting: boolean;
  completedApp: string | null;
  onPipelineComplete?: (nm: string) => void;
  finalMsg: string | null;
}> = ({ resp, submitting, completedApp, onPipelineComplete, finalMsg }) => {
  if (!resp && submitting) {
    return (
      <div style={{
        padding: 24, borderRadius: 12, border: "1px solid #c8d4e0",
        background: "#fff", display: "flex", alignItems: "center", gap: 14,
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: "50%",
          border: "3px solid #c8d4e0", borderTopColor: "#1e3a6e",
          animation: "dip-spin 0.8s linear infinite",
        }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Initialising pipeline…</div>
          <div style={{ fontSize: 12, color: "#445566" }}>Uploading and registering jobs.</div>
        </div>
      </div>
    );
  }
  if (!resp) return null;

  const planned = resp.pipeline_definition.filter(d => d.job_type in resp.jobs);
  const total   = planned.length;
  const done    = planned.filter(d => toSt(resp.jobs[d.job_type]?.status) === "success").length;
  const hasFail = planned.some(d => toSt(resp.jobs[d.job_type]?.status) === "failed");
  const running = planned.some(d => toSt(resp.jobs[d.job_type]?.status) === "running");
  const pct     = total > 0 ? Math.round((done / total) * 100) : 0;
  const status  = hasFail ? "failed" : (done === total && total > 0) ? "complete" : running ? "running" : "queued";

  return (
    <div style={{
      borderRadius: 12, border: "1px solid #c8d4e0",
      background: "#fff", overflow: "hidden",
    }}>
      <div style={{
        padding: "16px 20px",
        background: status === "failed" ? "#fef2f2"
                  : status === "complete" ? "#f0fdf4"
                  : "#dde7f5",
        borderBottom: "1px solid #c8d4e0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: "1.5px", textTransform: "uppercase", color: "#445566", marginBottom: 4 }}>
            Pipeline tracker
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color:
            status === "failed" ? "#991b1b"
            : status === "complete" ? "#166534"
            : "#1e3a6e",
          }}>
            {status === "failed" ? "Failed"
             : status === "complete" ? "Complete"
             : status === "running" ? "Running…"
             : "Queued"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: "#1e3a6e" }}>
            {done}<span style={{ fontSize: 14, color: "#445566" }}>/{total}</span>
          </div>
          <div style={{ fontSize: 11, color: "#445566" }}>steps · {pct}%</div>
        </div>
      </div>

      <div style={{ padding: 14 }}>
        {planned.map((d) => {
          const job = resp.jobs[d.job_type];
          const st  = toSt(job?.status);
          const elapsed = dur(job?.start_time, job?.end_time);
          return (
            <div key={d.job_type} style={{
              display: "flex", gap: 10, padding: "8px 12px",
              borderRadius: 8, marginBottom: 4,
              background: st === "running" ? "#dde7f5"
                        : st === "success" ? "#f0fdf4"
                        : st === "failed"  ? "#fef2f2"
                        : "transparent",
              border: "1px solid",
              borderColor: st === "running" ? "#c8d4e0"
                        :  st === "success" ? "#86efac"
                        :  st === "failed"  ? "#fca5a5"
                        :  "transparent",
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%", marginTop: 6,
                background: st === "success" ? "#16a34a"
                          : st === "failed"  ? "#dc2626"
                          : st === "running" ? "#1e3a6e"
                          : "#cbd5e1",
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: "#0f1923" }}>{d.label}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {elapsed && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "#445566" }}>{elapsed}</span>}
                    <span className={cls("gw-mode-badge",
                      st === "success" ? "enriched"
                      : st === "failed" ? "rejected"
                      : st === "running" ? "raw"
                      : "")}>
                      {st === "yet_to_start" ? "queued"
                       : st === "running" ? "running"
                       : st === "success" ? "done"
                       : st === "failed" ? "failed"
                       : st}
                    </span>
                  </div>
                </div>
                {st === "failed" && job?.message && (
                  <div style={{ fontSize: 11.5, color: "#991b1b", marginTop: 4 }}>{job.message}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {completedApp && (
        <div style={{
          margin: "0 14px 14px", padding: 14, borderRadius: 10,
          background: "#f0fdf4", border: "1px solid #86efac",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#166534" }}>✅ Pipeline complete</div>
            <div style={{ fontSize: 12, color: "#16a34a", fontFamily: "'JetBrains Mono', monospace" }}>{completedApp}</div>
          </div>
          <button
            className="gw-btn gw-btn-success"
            onClick={() => onPipelineComplete?.(completedApp)}
            disabled={!onPipelineComplete}
          >
            Continue →
          </button>
        </div>
      )}

      {finalMsg && resp.status === "failed" && (
        <div style={{
          margin: "0 14px 14px", padding: 12, borderRadius: 8,
          background: "#fef2f2", border: "1px solid #fca5a5",
          color: "#991b1b", fontSize: 12.5,
        }}>
          {finalMsg}
        </div>
      )}

      <style>{`@keyframes dip-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default DataInputPage;